import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Loader2, ArrowLeft, Play, Lock } from 'lucide-react';
import { resolvePlayable, pickSource, clipName } from '../lib/media';
import { SkipControls, canSkip } from '../components/SkipControls';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BONE = '#f5e8c7';
const GOLD = '#c8a672';

const EventWatchPage = () => {
  const { eventId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, event: null, videos: [], error: null, needSub: false });
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const load = async () => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (!token) {
        setState({ loading: false, event: null, videos: [], error: 'auth', needSub: false });
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/events/${eventId}/watch`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setState({ loading: false, event: res.data.event, videos: res.data.videos || [], error: null, needSub: false });
        // Deep-link support: `?v=<id>` jumps straight to that clip if it's
        // in the event, otherwise falls back to the admin-set opening.
        const deepLink = search.get('v');
        const hasDeep = deepLink && (res.data.videos || []).some((v) => v.id === deepLink);
        setActiveId(hasDeep ? deepLink : (res.data.opening_video_id || (res.data.videos?.[0]?.id ?? null)));
      } catch (e) {
        if (e.response?.status === 402) {
          setState({ loading: false, event: null, videos: [], error: null, needSub: true });
        } else if (e.response?.status === 401) {
          setState({ loading: false, event: null, videos: [], error: 'auth', needSub: false });
        } else {
          setState({ loading: false, event: null, videos: [], error: 'notfound', needSub: false });
        }
      }
    };
    load();
  }, [eventId, search]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#0a0705] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: BONE }} />
      </div>
    );
  }

  if (state.error === 'auth') {
    return (
      <div className="min-h-screen bg-[#0a0705] text-white flex flex-col items-center justify-center p-8 text-center">
        <Lock className="w-10 h-10 mb-4" style={{ color: GOLD }} />
        <h1 className="text-2xl font-semibold mb-2">Sign in to watch</h1>
        <p className="text-white/70 mb-6 max-w-md">
          This event is available to Elevated members. Sign in to pick up where you left off, or start a subscription.
        </p>
        <div className="flex gap-3">
          <Link to="/login" className="px-5 py-2.5 rounded-md font-semibold" style={{ background: BONE, color: '#0a0705' }} data-testid="watch-signin">
            Sign in
          </Link>
          <Link to="/subscription" className="px-5 py-2.5 rounded-md font-semibold" style={{ background: GOLD, color: '#0a0705' }} data-testid="watch-subscribe">
            Subscribe
          </Link>
        </div>
      </div>
    );
  }

  if (state.needSub) {
    return (
      <div className="min-h-screen bg-[#0a0705] text-white flex flex-col items-center justify-center p-8 text-center">
        <Lock className="w-10 h-10 mb-4" style={{ color: GOLD }} />
        <h1 className="text-2xl font-semibold mb-2">Subscriber-only event</h1>
        <p className="text-white/70 mb-6 max-w-md">
          Your Elevated membership gives you unlimited access to events like this. Upgrade in under a minute.
        </p>
        <Link to="/subscription" className="px-5 py-2.5 rounded-md font-semibold" style={{ background: GOLD, color: '#0a0705' }} data-testid="watch-subscribe">
          Subscribe
        </Link>
      </div>
    );
  }

  if (state.error === 'notfound' || !state.event) {
    return (
      <div className="min-h-screen bg-[#0a0705] text-white flex flex-col items-center justify-center p-8 text-center">
        <h1 className="text-xl font-semibold mb-2">Event not found</h1>
        <Button variant="outline" onClick={() => navigate('/')} data-testid="watch-back-home">Back to home</Button>
      </div>
    );
  }

  const { event, videos } = state;
  const active = videos.find((v) => v.id === activeId) || videos[0];
  const playable = resolvePlayable(pickSource(active));
  const getTarget = () => document.querySelector('[data-testid="watch-player"] video, [data-testid="watch-player"] iframe');
  const displayTitle = event.homepage_title || event.title;
  const displayDesc = event.homepage_description || event.description || '';

  return (
    <div className="min-h-screen bg-[#0a0705] text-white" data-testid="event-watch-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-4"
          data-testid="watch-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Main player */}
        <div className="aspect-video bg-black rounded-xl overflow-hidden border border-white/10" data-testid="watch-player">
          {playable.kind !== 'none' ? (
            playable.kind === 'video' ? (
              <video key={playable.src} src={playable.src} controls autoPlay playsInline preload="auto" controlsList="nodownload" className="w-full h-full" />
            ) : (
              <iframe
                key={playable.src}
                src={playable.src}
                title={clipName(active) || displayTitle}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/60">
              No video available for this event yet.
            </div>
          )}
        </div>
        {canSkip(playable.kind, playable.src) && (
          <SkipControls getTarget={getTarget} className="mt-3" testidPrefix="watch-skip" />
        )}

        {/* Event meta */}
        <div className="mt-6">
          <h1 className="text-2xl sm:text-3xl font-semibold">{displayTitle}</h1>
          {clipName(active) && (
            <p className="text-sm text-white/60 mt-1" data-testid="watch-now-playing">Now playing: {clipName(active)}</p>
          )}
          {displayDesc && (
            <p className="text-white/80 mt-3 leading-relaxed max-w-3xl">{displayDesc}</p>
          )}
        </div>

        {/* Playlist */}
        <div className="mt-8">
          <h2 className="text-xs tracking-[0.3em] uppercase text-white/60 mb-3">In this event</h2>
          {videos.length === 0 ? (
            <p className="text-sm text-white/60">No videos have been attached to this event yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="watch-playlist">
              {videos.map((v) => {
                const isActive = v.id === active?.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setActiveId(v.id)}
                    className={`text-left rounded-lg overflow-hidden border transition-all ${isActive ? 'border-[color:var(--gold)]' : 'border-white/10 hover:border-white/30'}`}
                    style={{ background: '#141010', '--gold': GOLD }}
                    data-testid={`watch-playlist-item-${v.id}`}
                  >
                    <div className="aspect-video bg-black relative">
                      {v.thumbnail_url ? (
                        <img src={v.thumbnail_url} alt={clipName(v)} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">
                          No thumbnail
                        </div>
                      )}
                      {isActive && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm" style={{ background: GOLD, color: '#0a0705' }}>
                            Now Playing
                          </span>
                        </div>
                      )}
                      {!isActive && (
                        <div className="absolute inset-0 opacity-0 hover:opacity-100 bg-black/40 flex items-center justify-center transition-opacity">
                          <Play className="w-8 h-8" style={{ color: BONE }} />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-medium truncate">{clipName(v) || displayTitle}</div>
                      {Array.isArray(v.tags) && v.tags.length > 0 && (
                        <div className="text-xs text-white/60 truncate mt-1">{v.tags.slice(0, 3).join(' · ')}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventWatchPage;
